/**
 * MessageTemplateBoard — Admin > Forms & Templates > SMS Templates and Email
 * Templates: the corporate dashboard's pages for one account, as a
 * drag-and-drop board (CategoryBoard, like Products):
 *   - categories A→Z, each holding its templates A→Z; drag a template onto
 *     another category to move it there (saved on drop through POST
 *     /…/reorder, last in that category's saved order; 2026-10-07, user: the
 *     board files, it no longer orders);
 *   - "Add category" top right; on a category row: Add template, Edit (name),
 *     Delete — only once it holds no live templates, because a deleted
 *     category can't be restored (the API has no restore) and its templates
 *     would disappear with it;
 *   - columns Name and Message (SMS) / Subject (email), as corp;
 *   - search (name and message / subject) and Show deleted. As corp, a
 *     template in a deleted category shows only under Show deleted (in
 *     Uncategorized, with corp's "Category Deleted" badge); one with no
 *     category, or one that no longer exists, is listed in Uncategorized and
 *     can be dragged into a category.
 * Corp has no category list, ordering or category delete; its category
 * Add / Update lived inside the template dialog.
 *
 * On a Locked page the shared blocks drop Add category, the row icons and
 * drag (AdminBoardPage, AdminRowAction, CategoryBoard), a template still
 * opens read-only (AdminFormModal), and this page drops its own drag and
 * "add a category" wording.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { getErrorMessage } from '@/services/apiClient';
import {
  createMessageTemplateCategory,
  deleteMessageTemplate,
  deleteMessageTemplateCategory,
  listMessageTemplateCategories,
  listMessageTemplates,
  renameMessageTemplateCategory,
  reorderMessageTemplates,
  restoreMessageTemplate,
  type MessageTemplate,
  type MessageTemplateCategory,
  type MessageTemplateKind,
} from '@/services/messageTemplatesAdmin.service';
import { toast } from '@/store/toastStore';
import { AdminBoardPage, AdminListEmpty, AdminListToolbar } from './AdminList';
import { useAdminPageLevel } from './AdminPageLevelContext';
import { AdminRowAction } from './AdminRowActions';
import { CategoryBoard, boardDragHint, type BoardCategory } from './CategoryBoard';
import { CategoryNameModal } from './CategoryNameModal';
import { compareTitles, isSeparatorTitle } from './categoryBoardState';
import { OutlineBadge } from './CorpBadges';
import { MESSAGE_TEMPLATE_NOUN, MessageTemplateFormModal } from './MessageTemplateFormModal';

interface KindText {
  title: string;
  subtitle: string;
  searchPlaceholder: string;
  bodyColumn: string;
  plural: string;
}

const TEXT: Record<MessageTemplateKind, KindText> = {
  sms: {
    title: 'SMS Templates',
    subtitle: 'SMS templates grouped by category.',
    searchPlaceholder: 'Search by name or message...',
    bodyColumn: 'Message',
    plural: 'SMS templates',
  },
  email: {
    title: 'Email Templates',
    subtitle: 'Email templates grouped by category.',
    searchPlaceholder: 'Search by name or subject...',
    bodyColumn: 'Subject',
    plural: 'email templates',
  },
};

/** Added to the subtitle while templates can be dragged (not on a Locked page). */
const DRAG_SUBTITLE = 'Drag a template onto another category to move it there.';

function templateName(t: MessageTemplate): string {
  return t.name.trim() || `Template #${t.id}`;
}

function bodyText(kind: MessageTemplateKind, t: MessageTemplate): string {
  return kind === 'sms' ? t.message : t.subject;
}

function compareTemplates(a: MessageTemplate, b: MessageTemplate): number {
  return a.order - b.order || compareTitles(a.name, b.name) || a.id - b.id;
}

const getRecordId = (t: MessageTemplate) => t.id;
const getRecordTitle = (t: MessageTemplate) => t.name;
const getRecordCategoryId = (t: MessageTemplate) => t.categoryId;
const isDeleted = (t: MessageTemplate) => !t.active;

type FormState = { template: MessageTemplate | null; category: { id: number; name: string } | null } | null;
type CategoryModalState = { current: { id: number; name: string } | null } | null;

export function MessageTemplateBoard({ kind }: { kind: MessageTemplateKind }) {
  const text = TEXT[kind];
  const noun = MESSAGE_TEMPLATE_NOUN[kind];
  const locked = useAdminPageLevel() === 'locked';
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [categories, setCategories] = useState<MessageTemplateCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [formState, setFormState] = useState<FormState>(null);
  const [categoryModal, setCategoryModal] = useState<CategoryModalState>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<MessageTemplate | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<BoardCategory | null>(null);

  // Only the latest load may write.
  const loadSeq = useRef(0);
  const load = useCallback(
    async (quiet = false) => {
      const seq = ++loadSeq.current;
      if (!quiet) setLoading(true);
      try {
        const [list, cats] = await Promise.all([listMessageTemplates(kind), listMessageTemplateCategories(kind)]);
        if (seq !== loadSeq.current) return;
        setTemplates(list);
        setCategories(cats);
      } catch (err) {
        if (seq === loadSeq.current) toast.error(getErrorMessage(err, `Failed to load ${text.plural}`));
      } finally {
        if (seq === loadSeq.current) setLoading(false);
      }
    },
    [kind, text.plural],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // ── Board data ──────────────────────────────────────────────────────────────

  const liveCategoryIds = useMemo(() => new Set(categories.map((c) => c.id)), [categories]);
  const allBoardCategories = useMemo(
    (): BoardCategory[] =>
      [...categories]
        .filter((c) => !isSeparatorTitle(c.name))
        .sort((a, b) => compareTitles(a.name, b.name))
        .map((c) => ({ id: c.id, title: c.name })),
    [categories],
  );

  // A template in a deleted category (as a deleted template) shows only under Show deleted.
  const inDeletedCategory = useCallback((t: MessageTemplate) => t.categoryActive === false && !liveCategoryIds.has(t.categoryId ?? 0), [liveCategoryIds]);
  const listed = useMemo(
    () => templates.filter((t) => showDeleted || (t.active && !inDeletedCategory(t))).sort(compareTemplates),
    [templates, showDeleted, inDeletedCategory],
  );

  const query = search.trim().toLowerCase();
  const searching = query !== '';
  const records = useMemo(
    () =>
      searching
        ? listed.filter((t) => t.name.toLowerCase().includes(query) || bodyText(kind, t).toLowerCase().includes(query))
        : listed,
    [listed, searching, query, kind],
  );
  const boardCategories = useMemo(() => {
    if (!searching) return allBoardCategories;
    const withMatches = new Set(records.map((t) => t.categoryId));
    return allBoardCategories.filter((c) => withMatches.has(c.id));
  }, [allBoardCategories, records, searching]);

  /** Live templates filed under a category — a category is deleted only once it has none. */
  const liveIn = useCallback((categoryId: number) => templates.filter((t) => t.active && t.categoryId === categoryId).length, [templates]);

  // ── Saves ───────────────────────────────────────────────────────────────────

  const handleReorder = useCallback(
    async (categoryId: number, ids: number[]) => {
      await reorderMessageTemplates(kind, categoryId, ids);
      setTemplates((prev) =>
        prev.map((t) => {
          const at = ids.indexOf(t.id);
          return at < 0 ? t : { ...t, categoryId, categoryActive: true, order: at + 1 };
        }),
      );
    },
    [kind],
  );

  const handleSaveError = useCallback((err: unknown) => {
    toast.error(getErrorMessage(err, 'The new order could not be saved.'));
  }, []);

  function handleTemplateSaved(saved: MessageTemplate | null, isNew: boolean) {
    setFormState(null);
    if (!saved) {
      void load(true);
      return;
    }
    setTemplates((prev) => (isNew ? [...prev, saved] : prev.map((t) => (t.id === saved.id ? { ...t, ...saved } : t))));
  }

  async function handleDeleteTemplate() {
    if (!deletingTemplate) return;
    const id = deletingTemplate.id;
    try {
      await deleteMessageTemplate(kind, id);
      setTemplates((prev) => prev.map((t) => (t.id === id ? { ...t, active: false } : t)));
      toast.success(`"${templateName(deletingTemplate)}" deleted`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Delete failed'));
    }
  }

  async function handleRestoreTemplate(template: MessageTemplate) {
    try {
      await restoreMessageTemplate(kind, template.id);
      setTemplates((prev) => prev.map((t) => (t.id === template.id ? { ...t, active: true } : t)));
      toast.success(`${templateName(template)} restored`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Restore failed'));
    }
  }

  async function handleCategorySave(name: string) {
    const current = categoryModal?.current ?? null;
    if (current) {
      await renameMessageTemplateCategory(kind, current.id, name);
      setCategories((prev) => prev.map((c) => (c.id === current.id ? { ...c, name } : c)));
      toast.success('Category updated');
    } else {
      const saved = await createMessageTemplateCategory(kind, name);
      if (saved.id) setCategories((prev) => [...prev, saved]);
      else void load(true);
      toast.success('Category created');
    }
    setCategoryModal(null);
  }

  async function handleDeleteCategory() {
    if (!deletingCategory) return;
    const id = deletingCategory.id;
    try {
      await deleteMessageTemplateCategory(kind, id);
      setCategories((prev) => prev.filter((c) => c.id !== id));
      toast.success('Category deleted');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The category could not be deleted.'));
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  const renderRecord = useCallback(
    (t: MessageTemplate) => {
      const deleted = !t.active;
      const body = bodyText(kind, t);
      return (
        <button
          type="button"
          // A deleted template is restored, not edited.
          disabled={deleted}
          onClick={() => setFormState({ template: t, category: null })}
          className="flex w-full items-center gap-3 rounded py-2 pr-2 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:hover:bg-transparent"
        >
          <span className="flex w-[38%] min-w-0 shrink-0 items-center gap-2">
            <span data-testid="template-name" className="truncate text-sm font-medium text-slate-700">
              {templateName(t)}
            </span>
            {inDeletedCategory(t) && (
              <OutlineBadge tone="orange" className="shrink-0">
                Category Deleted
              </OutlineBadge>
            )}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm text-slate-500" title={body || undefined}>
            {body || '—'}
          </span>
        </button>
      );
    },
    [kind, inDeletedCategory],
  );

  function renderCategoryActions(c: BoardCategory) {
    const count = liveIn(c.id);
    return (
      <>
        <AdminRowAction
          kind="add"
          title={`Add ${noun}`}
          label={`Add ${noun} to ${c.title}`}
          onClick={() => setFormState({ template: null, category: { id: c.id, name: c.title } })}
        />
        <AdminRowAction kind="edit" label={`Edit ${c.title}`} onClick={() => setCategoryModal({ current: { id: c.id, name: c.title } })} />
        <AdminRowAction
          kind="delete"
          label={`Delete ${c.title}`}
          blockedReason={count > 0 ? `Holds ${count} ${count === 1 ? noun : `${noun}s`} — move or delete them first` : null}
          onClick={() =>
            count > 0 ? toast.error(`Move or delete the ${noun}s in "${c.title}" before deleting it.`) : setDeletingCategory(c)
          }
        />
      </>
    );
  }

  function renderRecordActions(t: MessageTemplate) {
    const name = templateName(t);
    if (!t.active) return <AdminRowAction kind="restore" label={`Restore ${name}`} onClick={() => void handleRestoreTemplate(t)} />;
    return (
      <>
        <AdminRowAction kind="edit" label={`Edit ${name}`} onClick={() => setFormState({ template: t, category: null })} />
        <AdminRowAction kind="delete" label={`Delete ${name}`} onClick={() => setDeletingTemplate(t)} />
      </>
    );
  }

  const columnHeader = (
    <div className="flex items-center gap-3 pr-2">
      <span className="w-[38%] shrink-0">Name</span>
      <span className="min-w-0 flex-1">{text.bodyColumn}</span>
    </div>
  );

  const editingCategory = formState?.template
    ? (categories.find((c) => c.id === formState.template?.categoryId) ?? null)
    : null;

  return (
    <AdminBoardPage
      title={text.title}
      subtitle={locked ? text.subtitle : `${text.subtitle} ${DRAG_SUBTITLE}`}
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCategoryModal({ current: null })}>
          Add category
        </Button>
      }
      toolbar={
        <AdminListToolbar
          className="mb-0"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={text.searchPlaceholder}
          searchLabel={`Search ${text.plural}`}
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId={`${kind}-templates-show-deleted`}
        />
      }
      hint={boardDragHint({ searching, filtering: false, showDeleted })}
      dialogs={
        <>
          {formState && (
            <MessageTemplateFormModal
              key={formState.template ? `edit-${formState.template.id}` : `new-${formState.category?.id}`}
              kind={kind}
              template={formState.template}
              category={
                formState.template
                  ? editingCategory
                    ? { id: editingCategory.id, name: editingCategory.name }
                    : null
                  : formState.category
              }
              onClose={() => setFormState(null)}
              onSaved={handleTemplateSaved}
            />
          )}
          {categoryModal && (
            <CategoryNameModal
              current={categoryModal.current}
              categories={categories}
              recordNoun={noun}
              onSave={handleCategorySave}
              onClose={() => setCategoryModal(null)}
            />
          )}
          <ConfirmDialog
            isOpen={deletingTemplate !== null}
            onClose={() => setDeletingTemplate(null)}
            onConfirm={handleDeleteTemplate}
            title={deletingTemplate ? `Delete "${templateName(deletingTemplate)}"?` : ''}
            message={`It will no longer be offered when sending. You can restore it from Show deleted.`}
            confirmLabel="Delete template"
            variant="danger"
          />
          <ConfirmDialog
            isOpen={deletingCategory !== null}
            onClose={() => setDeletingCategory(null)}
            onConfirm={handleDeleteCategory}
            title={deletingCategory ? `Delete "${deletingCategory.title}"?` : ''}
            message="The category is removed for good: deleted categories can't be restored."
            confirmLabel="Delete category"
            variant="danger"
          />
        </>
      }
    >
      {loading ? (
        <SpinnerOverlay label={`Loading ${text.plural}…`} />
      ) : searching && records.length === 0 ? (
        <AdminListEmpty title={`No ${text.plural} found`} hint="Try adjusting your search." />
      ) : (
        <>
          {!searching && boardCategories.length === 0 && records.length === 0 && (
            <AdminListEmpty title="No categories yet" hint={locked ? undefined : `Add a category, then add ${text.plural} to it.`} />
          )}
          <CategoryBoard<MessageTemplate>
            categories={boardCategories}
            records={records}
            getRecordId={getRecordId}
            getRecordTitle={getRecordTitle}
            getRecordCategoryId={getRecordCategoryId}
            renderRecord={renderRecord}
            columnHeader={columnHeader}
            renderCategoryActions={renderCategoryActions}
            renderRecordActions={renderRecordActions}
            isRecordDeleted={isDeleted}
            onReorderRecords={handleReorder}
            onSaveError={handleSaveError}
            recordNoun={[noun, `${noun}s`]}
            dragDisabled={searching || showDeleted}
          />
        </>
      )}
    </AdminBoardPage>
  );
}
